import React, { FC, useState } from 'react';
import { Switch } from 'antd';
import { Formik, FieldArray, Form, useFormikContext } from 'formik';
import COButton from 'shared/components/COButton';
import InputField from 'shared/components/InputField';
import OrganizationModal from 'shared/components/OrganizationModal';
import { baselineYearList } from 'views/CompanySetup/companySetupConstants';
import { validationSchemaYears } from '../editLocationValidationSchema';
import { ReactComponent as ErrorIcon } from 'assets/icons/warning_triangle.svg';
import { Location } from 'models/ActivityDataEntryLocation/location.model';
import styles from './boundaryModal.module.scss';
import { LocationYears } from 'models/ActivityDataLocationYears/locationYears.model';
import { allowOnlyNumbers } from 'shared/utils/convertFormData';

interface OrganizationBoundaryModalProps {
  isEdiBoundaryModalOpen: boolean;
  handleModalClose: () => void;
  setIsEditBoundaryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLocationEdit: boolean;
  yearsWithScopeStatus?: LocationYears[];
}

const OrganizationBoundaryModal: FC<OrganizationBoundaryModalProps> = ({
  isEdiBoundaryModalOpen,
  handleModalClose,
  setIsEditBoundaryModalOpen,
  isLocationEdit,
  yearsWithScopeStatus,
}) => {
  const [isBoundaryError, setIsBoundaryError] = useState(false);

  const { values, setFieldValue } = useFormikContext<Location>();

  const handleBoundarySubmit =
    (setFieldValue?: Function) => (values: { years: LocationYears[] }) => {
      const isError = values?.years?.some(item => item?.isActive);
      if (!isError) {
        setIsBoundaryError(true);
        return;
      } else {
        setFieldValue?.(`yearsWithScopeStatus`, values?.years);
        setIsBoundaryError(false);
        setIsEditBoundaryModalOpen(false);
      }
    };

  const handleSwitchOnChange =
    (setFieldValue: Function, index: number, values: LocationYears[]) =>
    (checked: boolean) => {
      setFieldValue(`years[${index}].isActive`, checked);
      if (values[index]?.id) return;
      if (checked) {
        setFieldValue(`years[${index}].sharePercentage`, 100);
      } else {
        setFieldValue(`years[${index}].sharePercentage`, '');
      }
    };
  return (
    <OrganizationModal
      visible={isEdiBoundaryModalOpen}
      onClose={handleModalClose}
      width={isLocationEdit ? 500 : 350}
      children={
        <>
          {isLocationEdit ? (
            <div className={styles.editBoundaryContainer}>
              <div className={styles.title}>
                Organizational boundary details
              </div>
              <div className={styles.subtitle}>
                Enter equity share value for each year ( unit as Percentage% )
              </div>
              <Formik
                initialValues={{
                  years: values?.yearsWithScopeStatus ?? [],
                }}
                enableReinitialize={true}
                validationSchema={validationSchemaYears}
                onSubmit={handleBoundarySubmit(setFieldValue)}>
                {({ values, setFieldValue }) => (
                  <Form>
                    {isBoundaryError && (
                      <div className={styles.errorMessageContainer}>
                        <div className={styles.errorMessageIcon}>
                          {/* TODO I'll add icon after designer provide */}
                          <ErrorIcon />
                        </div>
                        <div className={styles.errorMessageText}>
                          Please enable location access for one year, as it’s
                          not possible to have it disabled for all years.
                        </div>
                      </div>
                    )}
                    <div className={styles.yearsForm}>
                      <FieldArray
                        name="years"
                        render={() => (
                          <>
                            {baselineYearList?.map((yearObj, index) => (
                              <div
                                key={yearObj.key}
                                className={styles.yearFormContainer}>
                                <div className={styles.switch}>
                                  <Switch
                                    checked={values?.years[index]?.isActive}
                                    onChange={handleSwitchOnChange(
                                      setFieldValue,
                                      index,
                                      values?.years
                                    )}
                                  />
                                </div>
                                <p className={styles.yearLabel}>
                                  {yearObj.label}
                                </p>
                                <InputField
                                  name={`years[${index}].sharePercentage`}
                                  placeholderstring=""
                                  disabled={!values?.years[index]?.isActive}
                                  onKeyDown={allowOnlyNumbers}
                                />
                              </div>
                            ))}
                          </>
                        )}
                      />
                    </div>
                    <div className={styles.buttonSubmitContainer}>
                      <COButton submit>Submit</COButton>
                      <COButton secondary onClick={handleModalClose}>
                        Back
                      </COButton>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          ) : (
            <div className={styles.boundaryModalContainer}>
              <div className={styles.boundaryModalTitle}>
                Organizational Boundary
              </div>
              {yearsWithScopeStatus
                ?.filter(item => item.isActive)
                .map(year => (
                  <div className={styles.boundaryDetails} key={year?.year}>
                    <div className={styles.boundaryDetailsLabel}>
                      {year?.year}
                    </div>
                    <div className={styles.boundaryDetailsValue}>
                      {year?.sharePercentage !== null
                        ? year?.sharePercentage + '%'
                        : '-'}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      }
    />
  );
};

export default OrganizationBoundaryModal;
